import React, { useState, useCallback, useMemo, Dispatch, SetStateAction } from "react";
import { t } from "@lingui/macro";
import { AnswerListPossibility } from "@WahooFitness/cloud-client-types";
import { Responses } from "@/components/Quiz/useQuiz";

interface Props {
  initialVal?: Array<number | null | string>;
  options: Array<AnswerListPossibility>;
  measurement?: string;
  setCurrentStepHasValue: (val: boolean) => void;
  setCurrentStepValid: (val: boolean) => void;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
}

function useMultiNumericEntry({
  initialVal,
  options,
  setCurrentStepHasValue,
  setCurrentStepValid,
  setResponses,
  questionId,
}: Props) {
  const isSkipInitial = initialVal?.[0] === "skip";

  const [values, setValues] = useState<Record<string, number | null>>(() =>
    options.reduce(
      (acc, option, index) => {
        const init = isSkipInitial ? initialVal?.[index + 1] : initialVal?.[index];
        acc[option.key] = typeof init === "number" ? init : null;
        return acc;
      },
      {} as Record<string, number | null>
    )
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [skipChecked, setSkipChecked] = useState(isSkipInitial);

  const fieldValidations = useMemo(
    () =>
      options.reduce(
        (acc, option) => {
          if (option.range) {
            acc[option.key] = { min: option.range.start, max: option.range.stop };
          }
          return acc;
        },
        {} as Record<string, { min: number; max: number }>
      ),
    [options]
  );

  const validateField = useCallback(
    (name: string, value: number | null): string | null => {
      if (value === null) return null;
      const validation = fieldValidations[name];
      if (!validation) return null;
      if (value < validation.min) return t`Value is too low`;
      if (value > validation.max) return t`Value is too high`;
      return null;
    },
    [fieldValidations]
  );

  const storeResponses = useCallback(
    (nextValues: Record<string, number | null>) => {
      setResponses((prev: Responses | undefined) => {
        if (!prev) {
          prev = {};
        }
        prev[questionId] = options.map((opt) => nextValues[opt.key]);
        return prev;
      });
    },
    [options, questionId, setResponses]
  );

  const handleInputChange = useCallback(
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | { target: { name: string; value: number | null } }
    ) => {
      const { name } = event.target;
      const rawValue = event.target.value;
      const value = typeof rawValue === "string" ? null : rawValue;
      setFieldErrors((prev) => {
        const next = { ...prev, [name]: null };
        setCurrentStepValid(Object.values(next).every((e) => e === null));
        return next;
      });
      setValues((prev) => {
        const next = { ...prev, [name]: value };
        setCurrentStepHasValue(Object.values(next).some((v) => v !== null));
        storeResponses(next);
        return next;
      });
    },
    [setCurrentStepHasValue, setCurrentStepValid, storeResponses]
  );

  const handleBlur = useCallback(
    (name: string) => {
      const error = validateField(name, values[name]);
      setFieldErrors((prev) => {
        const next = { ...prev, [name]: error };
        setCurrentStepValid(Object.values(next).every((e) => e === null));
        return next;
      });
    },
    [setCurrentStepValid, validateField, values]
  );

  const handleSkipToggle = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (ev.target.checked) {
        setSkipChecked(true);
        setCurrentStepHasValue(true);
        setCurrentStepValid(true);
        setFieldErrors({});
        setResponses((prev: Responses | undefined) => {
          if (!prev) {
            prev = {};
          }
          prev[questionId] = ["skip", ...options.map((opt) => values[opt.key])];
          return prev;
        });
      } else {
        setSkipChecked(false);
        const hasAny = Object.values(values).some((v) => v !== null);
        setCurrentStepHasValue(hasAny);
        const errors = Object.fromEntries(
          options.map((opt) => [opt.key, validateField(opt.key, values[opt.key])])
        );
        setFieldErrors(errors);
        setCurrentStepValid(Object.values(errors).every((e) => e === null));
        storeResponses(values);
      }
    },
    [
      options,
      questionId,
      setCurrentStepHasValue,
      setCurrentStepValid,
      setResponses,
      storeResponses,
      validateField,
      values,
    ]
  );

  return {
    values,
    fieldErrors,
    handleInputChange,
    handleBlur,
    skipChecked,
    handleSkipToggle,
  };
}

export default useMultiNumericEntry;
